import React from 'react';
import { FormattedMessage } from '../../util/reactIntl';
import { pathByRouteName } from '../../util/routes';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';

import {
  InlineTextButton,
  Logo,
  Heading,
  LayoutSingleColumn,
  PrimaryButton,
} from '../../components';

import css from './LoadableComponentErrorBoundary.module.css';

/**
 * Shown when the code for a page could not be loaded, even after one automatic reload.
 * Every way out of this page is a full page load, because that fetches the latest code.
 *
 * @param {Object} props
 * @param {string?} props.orderId set when the buyer arrived here right after checkout
 */
export const LoadableComponentErrorBoundaryPage = props => {
  const { orderId } = props;
  const routeConfiguration = useRouteConfiguration();
  const landingPagePath = pathByRouteName('LandingPage', routeConfiguration);
  const handleOnClick = () => {
    if (typeof window !== 'undefined') {
      window.location = landingPagePath;
    }
  };
  const handleRefresh = () => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  };

  const homepageLink = (
    <InlineTextButton onClick={handleOnClick}>
      <FormattedMessage id="LoadableComponentErrorBoundaryPage.homepageLink" />
    </InlineTextButton>
  );

  // P25028-129: the order already exists at this point, so the buyer must not think it failed.
  const messagePrefix = orderId
    ? 'LoadableComponentErrorBoundaryPage.orderPlaced'
    : 'LoadableComponentErrorBoundaryPage.newVersion';

  // A plain link (not an in-app link) makes the browser load the order page from scratch.
  const mainAction = orderId ? (
    <a
      className={css.actionLink}
      href={pathByRouteName('OrderDetailsPage', routeConfiguration, { id: orderId })}
    >
      <FormattedMessage id="LoadableComponentErrorBoundaryPage.viewOrder" />
    </a>
  ) : (
    <PrimaryButton className={css.action} type="button" onClick={handleRefresh}>
      <FormattedMessage id="LoadableComponentErrorBoundaryPage.refresh" />
    </PrimaryButton>
  );

  return (
    <div>
      <LayoutSingleColumn
        topbar={
          <div className={css.topbar}>
            <InlineTextButton onClick={handleOnClick}>
              <Logo className={css.logoMobile} layout="mobile" />
              <Logo className={css.logoDesktop} layout="desktop" />
            </InlineTextButton>
          </div>
        }
        footer={null}
      >
        <div className={css.root}>
          <div className={css.content}>
            <Heading as="h1" rootClassName={css.heading}>
              <FormattedMessage id={`${messagePrefix}Heading`} />
            </Heading>
            <p className={css.description}>
              <FormattedMessage id={`${messagePrefix}Description`} />
            </p>
            {mainAction}
            <p className={css.homepageInfo}>
              <FormattedMessage
                id="LoadableComponentErrorBoundaryPage.homepageInfo"
                values={{ link: homepageLink }}
              />
            </p>
          </div>
        </div>
      </LayoutSingleColumn>
    </div>
  );
};
